// ---- Interior cutaway ----
// Keeps the player visible inside multi-storey buildings. When you're standing
// inside a structure, this fades out:
//   1. everything ABOVE your current floor (upper slabs, battlements, the roof)
//      — so the ceiling doesn't hide you, and
//   2. the wall(s) BETWEEN you and the camera — recomputed every frame, so as
//      you orbit (Q/E) whichever walls would block the view melt away.
// Walls reappear when you leave the footprint or climb to the next level.
//
// It's a pure post-step over per-mesh tags emitted by the structure system
// (`mesh.userData.cut`, see structure/define.ts) — no per-building code. Any new
// structure with walkable floors gets this for free.
//
// Indoor presentation (camera dolly + exterior blackout) shares the same
// shelter predicate and eases a 0..1 weight — see indoorWeight() / isInsideActiveIndoor().

import * as THREE from "three";
import type { InteriorInfo, CutTag } from "./structure/define.ts";
import { STEP_UP } from "./physics.ts";

const HEADROOM = 2.5; // a mesh is "overhead" if its base is this far above your floor
const FADE = 10;      // opacity lerp rate (per second) — ~0.2s settle
// How far INSIDE the footprint your feet must be before cutaway engages. The
// footprint is derived from the floor slabs, which extend out to the OUTER wall
// face (the slab tucks under the walls, see castle.ts). So pressing against the
// *outside* of a perimeter wall lands you right at the footprint edge — we must
// require you to be a step INSIDE (past the wall line) so the walls only melt
// away once you've actually entered through a gap (the gateway), not whenever you
// brush an exterior wall. Must stay smaller than a wall's thickness so standing with
// your back to the inner wall face still counts as inside — 1.0 was fine for the
// castle (WT 1.4) but EXCEEDED the doc-driven structures (chapel WT 0.5, shed 0.35),
// so a player against an inner wall there fell outside the footprint and the roof
// flickered back. 0.6 stays > PLAYER_RADIUS (0.45), so brushing a wall's OUTSIDE
// still keeps a margin, while giving the thin-walled structures interior slack.
const INSET = 0.6;

// Indoor enter presentation (camera + blackout) — eased on the same FADE clock
// as wall cutaways so enter/exit feel tied together. Purely client-visual.
const INDOOR_FADE = FADE;
/** Follow-camera dolly scale while fully indoors (~25% closer than outdoor). */
export const INDOOR_ZOOM = 0.75;
/** Fog near/far (camera-distance) at full indoor blackout — short outdoor
 *  doorway glimpse, then black. Weather may already have shrunk fog; we only
 *  shrink further (never expand past the outdoor grade). */
export const INDOOR_FOG_NEAR = 10;
export const INDOOR_FOG_FAR = 28;
export const INDOOR_SKY = 0x000000;

type Cut = CutTag & { mesh: THREE.Mesh; cur: number };
// `settled` = every cut material fully restored (opacity 1) — while true AND the
// player is far from the footprint, the whole per-mesh pass is skipped (a castle
// is 100+ tagged meshes; there's no reason to walk them from across the map).
type Reg = { it: InteriorInfo; cuts: Cut[]; settled: boolean };

// How far outside a footprint the player must be before its pass can be skipped
// (generous: fades finish long before you're this far away).
const SKIP_MARGIN = 40;

let regs: Reg[] = [];

// Footprints we're currently cutting into, with the floor the player is on. Other
// systems (NPCs, remote players) query this so anyone standing on a cut-away upper
// level disappears with it instead of floating in mid-air. Reused across frames.
type Active = { it: InteriorInfo; floor: number };
const active: Active[] = [];

/** Is (x,z) at least INSET inside a structure's footprint — i.e. past the wall
 *  line, properly inside, not just brushing an exterior wall? */
function inFootprint(it: InteriorInfo, x: number, z: number): boolean {
  return x > it.minX + INSET && x < it.maxX - INSET &&
         z > it.minZ + INSET && z < it.maxZ - INSET;
}

// The structure the PLAYER was sheltered by on the last updateCutaway pass —
// exactly the "cutting" condition: inside a footprint with a storey or roof
// above (null on an open top floor like the castle rampart). Weather reads
// this to GROW that structure's precip clip box while you're inside it: the
// camera orbits OUTSIDE the building and drops falling between it and the
// faded near wall (which no longer writes depth) — or above the cut-away
// ceiling — would otherwise render superimposed over the interior view.
const _shelter: ShelterBox = { minX: 0, maxX: 0, minZ: 0, maxZ: 0, yTop: 0 };
let sheltered = false;

/** The footprint sheltering the player (per the last updateCutaway pass), or
 *  null when under open sky. Returned object is reused — don't hold it. */
export function playerShelter(): ShelterBox | null {
  return sheltered ? _shelter : null;
}

// Eased 0..1 indoor presentation weight (target = sheltered this frame). Camera
// and atmosphere read this; entity hide uses the hard shelter + footprint test.
let indoorW = 0;

/** 0 outdoors → 1 fully indoors (eased). Safe to read before updateCutaway
 *  (previous frame's value) — follow camera uses it one frame behind cutaway. */
export function indoorWeight(): number {
  return indoorW;
}

/** True if (x,z) is inside the active indoor structure footprint. Uses the full
 *  shelter AABB (NO cutaway INSET) so entities against interior walls stay
 *  visible while the local player is indoors. False when not sheltered. */
export function isInsideActiveIndoor(x: number, z: number): boolean {
  if (!sheltered) return false;
  return x >= _shelter.minX && x <= _shelter.maxX &&
         z >= _shelter.minZ && z <= _shelter.maxZ;
}

/** True when the local player is indoors and this entity is outside the active
 *  structure — presentation-only hide (interest / wire unchanged). */
export function hideOutdoorWhileIndoor(x: number, z: number): boolean {
  return sheltered && !isInsideActiveIndoor(x, z);
}

/** True if an entity at (x,z,feetY) is on a level that's currently cut away — i.e.
 *  it stands above the player's floor inside a structure being looked into. */
export function occludedByCutaway(x: number, z: number, feetY: number): boolean {
  for (const a of active) {
    if (inFootprint(a.it, x, z) && feetY >= a.floor + HEADROOM) return true;
  }
  return false;
}

/**
 * Which storey a pair of feet is on inside a structure.
 *
 * `floors` on InteriorInfo is the sorted list of walkable slab heights
 * (ground = floors[0], second = floors[1], …) — derived automatically from
 * every `floor`/`floorSlab` the structure emitted. `ceiling` is the next floor
 * up, or `roofCeiling` on the top enclosed storey, or +Infinity on an open top.
 *
 * Returns null if feet aren't near any floor line (shouldn't happen while
 * `inside` for cutaway). Use this whenever you need "am I on storey N".
 */
export function storeyAt(
  it: InteriorInfo,
  feetY: number,
): { floor: number; ceiling: number; storeyIndex: number } | null {
  if (it.floors.length === 0) return null;
  let floor = it.floors[0]!;
  let storeyIndex = 0;
  for (let i = 0; i < it.floors.length; i++) {
    const f = it.floors[i]!;
    if (f <= feetY + STEP_UP) {
      floor = f;
      storeyIndex = i;
    }
  }
  const ceiling =
    it.floors.find((f) => f > floor + 0.5) ?? it.roofCeiling ?? Infinity;
  return { floor, ceiling, storeyIndex };
}

// ---- Roof clipping for weather ---------------------------------------------
// The precipitation shaders (src/weather.ts) kill drops inside nearby roofed
// volumes so rain/snow never falls through a ceiling — indoors stays dry while
// the storm stays visible past the walls and out the doorway. A structure's
// clip volume spans its footprint up to:
//   - `top` when the top floor is ENCLOSED (`roofCeiling` set — house, chapel,
//     shed): the whole structure is dry, and drops overhead visibly stop at
//     the roofline (correct even when cutaway has faded the roof itself), or
//   - the top WALKABLE floor when it's open-topped (the castle rampart): the
//     deck itself stays wet; only the storeys beneath it are sheltered.
export type ShelterBox = {
  minX: number; maxX: number; minZ: number; maxZ: number; yTop: number;
};

/** Where a structure's precip clip volume ends (see the block comment above). */
function clipTop(it: InteriorInfo): number {
  return it.roofCeiling !== undefined ? it.top : it.floors[it.floors.length - 1]!;
}

/** The roofed volumes whose footprint comes within `radius` of (x,z) — nearest
 *  first, at most `max` (the weather shaders hold a fixed uniform budget). */
export function shelterBoxesNear(
  x: number, z: number, radius: number, max: number,
): ShelterBox[] {
  const out: (ShelterBox & { d2: number })[] = [];
  for (const { it } of regs) {
    if (
      x < it.minX - radius || x > it.maxX + radius ||
      z < it.minZ - radius || z > it.maxZ + radius
    ) continue;
    const dx = x - (it.minX + it.maxX) / 2, dz = z - (it.minZ + it.maxZ) / 2;
    out.push({
      minX: it.minX, maxX: it.maxX, minZ: it.minZ, maxZ: it.maxZ,
      yTop: clipTop(it),
      d2: dx * dx + dz * dz,
    });
  }
  out.sort((a, b) => a.d2 - b.d2);
  return out.slice(0, max);
}

/** Register the built structures' interiors + taggable meshes (from world.ts). */
export function registerCutaways(
  list: { interior: InteriorInfo; cutaway: THREE.Mesh[] }[] | undefined,
) {
  regs = (list ?? [])
    .filter((s) => s.interior.floors.length > 0)
    .map((s) => ({
      it: s.interior,
      cuts: s.cutaway.map((mesh) => ({
        ...(mesh.userData.cut as CutTag),
        mesh,
        cur: 1,
      })),
      settled: true,
    }));
}

/** Run each frame after the camera is positioned. `cam` is the camera's world
 *  position; (px,pz,feetY) is the player's feet. */
export function updateCutaway(
  px: number, pz: number, feetY: number, cam: THREE.Vector3, dt: number,
) {
  const k = Math.min(1, dt * FADE);
  active.length = 0;
  for (const reg of regs) {
    const it = reg.it;
    // Far away and nothing mid-fade: skip this structure entirely.
    if (
      reg.settled &&
      (px < it.minX - SKIP_MARGIN || px > it.maxX + SKIP_MARGIN ||
       pz < it.minZ - SKIP_MARGIN || pz > it.maxZ + SKIP_MARGIN)
    ) {
      continue;
    }
    const inside =
      inFootprint(it, px, pz) && feetY >= it.floors[0]! - 1 && feetY <= it.top + 1 &&
      // Standing ON the roof (gravity plan): feet at/above the ceiling line are
      // OUTSIDE — no cutaway melts while you're on top of the building. Open
      // tops (no roofCeiling — the castle rampart) keep the old rule.
      feetY < (it.roofCeiling ?? Infinity) - 0.2;

    // Storey under our feet (floors[] = walkable slab heights, low→high).
    // See storeyAt() — single source for floor / ceiling / storeyIndex.
    let floor = it.floors[0]!;
    let ceiling = Infinity;
    // Cut when there's a level ABOVE you — either another floor, or (on the top
    // floor) an enclosing ceiling/roof (`roofCeiling`). An OPEN top floor (the
    // castle's rampart) has neither, so leave the walls and the view untouched.
    let cutting = false;
    if (inside) {
      const storey = storeyAt(it, feetY);
      if (storey) {
        floor = storey.floor;
        ceiling = storey.ceiling;
      }
      const topFloor = it.floors[it.floors.length - 1]!;
      cutting = floor < topFloor || (floor === topFloor && it.roofCeiling !== undefined);
      if (cutting) active.push({ it, floor });
    } else {
      // Still need a ceiling value if we early-continue… not used when !cutting.
      ceiling = it.floors.find((f) => f > floor + 0.5) ?? it.roofCeiling ?? Infinity;
    }

    const dcx = cam.x - px, dcz = cam.z - pz; // horizontal player→camera
    // Wall faces camera (corner pieces: either normal).
    const facesCam = (c: { nx: number; nz: number; nx2?: number; nz2?: number }) =>
      (c.nx !== 0 || c.nz !== 0) &&
      (c.nx * dcx + c.nz * dcz > 0 ||
        (c.nx2 !== undefined && c.nz2 !== undefined &&
          c.nx2 * dcx + c.nz2 * dcz > 0));
    const isWall = (c: { nx: number; nz: number; nx2?: number; nz2?: number }) =>
      c.nx !== 0 || c.nz !== 0 || c.nx2 !== undefined;

    // While cutting, the structure can't be settled; otherwise it is unless
    // some material is still fading back to 1 below.
    let restored = !cutting;
    for (const c of reg.cuts) {
      let target = 1;
      if (cutting) {
        if (c.over && c.y0 >= ceiling - 0.5) {
          // Storey ABOVE me: hide furniture / roof / non-floor overhead so the
          // room stays readable. Floor slabs + planks (floorSlab) ALWAYS stay —
          // you should still see the deck of the floor above. Walls above only
          // hide when they face the camera (side/back upper façades remain).
          if (c.floorSlab) {
            // keep visible
          } else if (!isWall(c) || facesCam(c)) {
            target = 0;
          }
        } else if (
          // A wall of MY storey whose outward face points at the camera.
          // Must extend INTO the air of this storey (y1 > floor + 0.3) — not
          // merely touch the slab from below — so ground-floor wall bands stay
          // solid when you're upstairs (full-height shells are storey-split at
          // build: see splitShellsByFloors in structure/define.ts).
          isWall(c) &&
          c.y0 < ceiling && c.y1 > floor + 0.3 &&
          facesCam(c)
        ) {
          target = 0;
        }
      }
      if (c.cur === target) continue; // settled — skip

      c.cur += (target - c.cur) * k;
      if (Math.abs(c.cur - target) < 0.02) c.cur = target;
      const mat = c.mesh.material as THREE.MeshToonMaterial;
      mat.opacity = c.cur;
      mat.transparent = c.cur < 0.999;
      mat.depthWrite = c.cur > 0.5;
      c.mesh.visible = c.cur > 0.02;
      if (c.cur !== 1) restored = false;
    }
    reg.settled = restored;
  }
  sheltered = active.length > 0;
  if (sheltered) {
    const it = active[0]!.it;
    _shelter.minX = it.minX; _shelter.maxX = it.maxX;
    _shelter.minZ = it.minZ; _shelter.maxZ = it.maxZ;
    _shelter.yTop = clipTop(it);
  }

  // Indoor presentation weight: ease toward sheltered so camera dolly + exterior
  // blackout settle on the same clock as wall cutaways (~0.2–0.3s).
  const indoorTarget = sheltered ? 1 : 0;
  const ik = Math.min(1, dt * INDOOR_FADE);
  indoorW += (indoorTarget - indoorW) * ik;
  if (Math.abs(indoorW - indoorTarget) < 0.02) indoorW = indoorTarget;
}
